import { useEffect, useState, type ReactNode } from "react";
import { hieraApi } from "../lib/api";
import { t, type Lang } from "../lib/i18n";
import { THEME_IDS, parseTheme, themeName, type ThemeId } from "../lib/theme";
import {
  readEditMode,
  readNotify,
  readToolbar,
  readWide,
  writeEditMode,
  writeNotify,
  writeToolbar,
  writeWide,
  type EditMode,
  type NotifyKey,
} from "../lib/prefs";

type Theme = ThemeId;

type Props = {
  lang: Lang;
  setLang: (lang: Lang) => void;
  theme: Theme;
  setTheme: (theme: Theme) => void;
  staff?: boolean;
};

function Row({ title, hint, extra, children }: { title: string; hint: string; extra?: ReactNode; children: ReactNode }) {
  return (
    <div className="set-row">
      <div>
        <strong>
          {title}
          {extra}
        </strong>
        <p>{hint}</p>
      </div>
      <div className="set-control">{children}</div>
    </div>
  );
}

function Switch({ on, label, onChange }: { on: boolean; label: string; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      className={on ? "switch is-on" : "switch"}
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
    >
      <span />
    </button>
  );
}

export function SettingsPage({ lang, setLang, theme, setTheme, staff }: Props) {
  const [wide, setWide] = useState(readWide);
  const [mode, setMode] = useState<EditMode>(readEditMode);
  const [bar, setBar] = useState(readToolbar);
  const [notes, setNotes] = useState(readNotify);
  const [regClosed, setRegClosed] = useState(false);
  const [regBusy, setRegBusy] = useState(false);

  useEffect(() => {
    if (!staff) return;
    void hieraApi
      .siteSettings()
      .then((s) => setRegClosed(s.registrationClosed))
      .catch(() => undefined);
  }, [staff]);

  function note(key: NotifyKey, on: boolean) {
    setNotes(writeNotify(key, on));
  }

  async function toggleRegistration(next: boolean) {
    setRegBusy(true);
    try {
      const res = await hieraApi.patchSiteSettings({ registrationClosed: next });
      setRegClosed(res.registrationClosed);
    } catch {
      /* keep previous */
    } finally {
      setRegBusy(false);
    }
  }

  return (
    <div className="settings pad">
      <h1>{t(lang, "settings")}</h1>
      {staff ? (
        <>
          <h2>{t(lang, "siteSection")}</h2>
          <Row
            title={t(lang, "regClosed")}
            hint={t(lang, "regClosedHint")}
            extra={regClosed ? <em className="set-badge set-badge--warn">{t(lang, "regClosed")}</em> : null}
          >
            <Switch
              on={regClosed}
              label={t(lang, "regClosed")}
              onChange={(on) => {
                if (regBusy) return;
                void toggleRegistration(on);
              }}
            />
          </Row>
        </>
      ) : null}
      <Row title={t(lang, "theme")} hint={t(lang, "themeHint")}>
        <select
          value={theme}
          aria-label={t(lang, "theme")}
          onChange={(e) => setTheme(parseTheme(e.target.value))}
        >
          {THEME_IDS.map((id) => (
            <option key={id} value={id}>
              {themeName(id)}
            </option>
          ))}
        </select>
      </Row>
      <Row title={t(lang, "language")} hint={t(lang, "languageHint")}>
        <select value={lang} aria-label={t(lang, "language")} onChange={(e) => setLang(e.target.value === "en" ? "en" : "ru")}>
          <option value="ru">Русский</option>
          <option value="en">English</option>
        </select>
      </Row>
      <Row title={t(lang, "pageWidth")} hint={t(lang, "pageWidthHint")}>
        <Switch
          on={wide}
          label={t(lang, "pageWidth")}
          onChange={(on) => {
            setWide(on);
            writeWide(on);
          }}
        />
      </Row>
      <Row title={t(lang, "editMode")} hint={t(lang, "editModeHint")}>
        <div className="mode-switch" role="group" aria-label={t(lang, "editMode")}>
          <button
            type="button"
            className={mode === "edit" ? "is-on" : ""}
            onClick={() => {
              setMode("edit");
              writeEditMode("edit");
            }}
          >
            {t(lang, "edit")}
          </button>
          <button
            type="button"
            className={mode === "read" ? "is-on" : ""}
            onClick={() => {
              setMode("read");
              writeEditMode("read");
            }}
          >
            {t(lang, "read")}
          </button>
        </div>
      </Row>
      <Row
        title={t(lang, "pinToolbar")}
        hint={t(lang, "pinToolbarHint")}
        extra={<em className="set-badge">{t(lang, "experimental")}</em>}
      >
        <Switch
          on={bar}
          label={t(lang, "pinToolbar")}
          onChange={(on) => {
            setBar(on);
            writeToolbar(on);
          }}
        />
      </Row>

      <h2>{t(lang, "emailNotes")}</h2>
      <Row title={t(lang, "notePage")} hint={t(lang, "notePageHint")}>
        <Switch on={notes.page} label={t(lang, "notePage")} onChange={(on) => note("page", on)} />
      </Row>
      <Row title={t(lang, "noteMention")} hint={t(lang, "noteMentionHint")}>
        <Switch on={notes.mentionPage} label={t(lang, "noteMention")} onChange={(on) => note("mentionPage", on)} />
      </Row>
      <Row title={t(lang, "noteMentionComment")} hint={t(lang, "noteMentionCommentHint")}>
        <Switch on={notes.mentionComment} label={t(lang, "noteMentionComment")} onChange={(on) => note("mentionComment", on)} />
      </Row>
      <Row title={t(lang, "noteComment")} hint={t(lang, "noteCommentHint")}>
        <Switch on={notes.comment} label={t(lang, "noteComment")} onChange={(on) => note("comment", on)} />
      </Row>
      <Row title={t(lang, "noteResolved")} hint={t(lang, "noteResolvedHint")}>
        <Switch on={notes.resolved} label={t(lang, "noteResolved")} onChange={(on) => note("resolved", on)} />
      </Row>
    </div>
  );
}
